<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              :to="{ name: `create-${route}` }"
            >{{
              $t('button.create')
            }}</b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(bindTelegram)="data">
            <!-- button -->
            <n-button-loading
              v-if="data.item.isMyAccount && !data.item.isBindTelegram"
              variant="primary"
              pill
              size="sm"
              @submit="handleBindTelegram(true, data.item.id)"
            >{{ $t('button.bind_now') }}</n-button-loading>
            <div
              v-else-if="data.item.isBindTelegram"
              style="display: flex; align-items: center; gap: 0.5rem;"
            >
              <n-button-loading
                variant="primary"
                pill
                size="sm"
                :loading="selectedUserId === data.item.id && loadingUnbindTelegram"
                @submit="handleBindTelegram(false, data.item.id)"
              >{{ $t('button.unbind') }}</n-button-loading>
              <a
                v-if="data.item.tgUsername || data.item.tgFullName"
                target="_blank"
                v-bind="data.item.tgUsername ? { href: `https://t.me/${data.item.tgUsername}` } : {}"
              >
                {{ data.item.tgFullName || data.item.tgUsername }}
              </a>
              <span v-else>-</span>
            </div>
            <template v-else>
              <b-button
                v-b-tooltip.hover
                :title="$t('button.copyLink')"
                variant="light"
                pill
                style="height: fit-content; width: fit-content; padding: 4px 8px; margin-right: 0.5rem;"
                size="sm"
                @click="copyToClipboard(data.item.bindTelegramUrl)"
              >
                <feather-icon icon="CopyIcon" />
              </b-button>
              <b-badge
                pill
                variant="danger"
                badge-glow
              >
                {{ $t('status.not_yet_bound') }}
              </b-badge>
            </template>

          </template>
          <template #cell(name)="data">
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <span v-if="data.item.isBot == true">
                <span v-if="data.item.botType == botType.ADVANCED"> {{ $t('chat.aiAssistant') }}</span>
                <span v-else> {{ $t('chat.chatBot') }}</span>
              </span>
              <span v-else> {{ data.item.name }}</span>
              <b-badge
                v-if="data.item.isBot == true"
                pill
                variant="info"
                badge-glow
              >
                bot
              </b-badge>
            </div>
          </template>

          <template #cell(isActive)="data">
            <b-badge
              v-if="data.item.isActive == 1"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.${data.item.isActive}`) }}
            </b-badge>
            <b-badge
              v-else-if="data.item.isActive == 2 && data.item.phone"
              pill
              variant="warning"
              badge-glow
            >
              {{ $t(`status.pending`) }}
            </b-badge>
            <b-badge
              v-else-if="data.item.isActive == 2"
              pill
              variant="info"
              badge-glow
            >
              {{ $t(`status.${data.item.isActive}`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`status.${data.item.isActive}`) }}
            </b-badge>
            &nbsp;&nbsp;
            <template v-if="data.item.isActive == 2 && !data.item.phone">
              <b-link
                v-if="data.item.isActive == 2 && !resendLoading"
                class="link-underline"
                @click="resend(data.item.email, data.index)"
              >
                {{ $t('button.remind') }}
              </b-link>
              <b-spinner
                v-if="data.item.isActive == 2 && resendLoading"
                small
              />
              <span v-if="data.item.isActive == 2">
                &nbsp;&nbsp;|&nbsp;
                <b-link
                  v-if="data.item.verifyToken"
                  class="link-underline"
                  :href="`/invitation/verify?token=${data.item.verifyToken.token}&phone=${data.item.phone || ''}`"
                  target="_blank"
                >
                  {{ $t('button.invitedLink') }}
                </b-link>
                <span
                  v-else
                  class="text-warning"
                >{{
                  $t('status.expired')
                }}</span>
              </span>
            </template>
          </template>
          <!-- <template #cell(role)="data">
            <span v-if="data.item.role">
              {{ getRoleName(data.item.role) }}
            </span>
          </template> -->
          <template #cell(department)="data">
            <li
              v-for="department in data.item.departments"
              :key="department.id"
            >
              {{ getDepartmentName(department) }}
            </li>
          </template>
          <template #cell(serviceCap)="data">
            <span v-if="data.item.serviceCap == null"> 5 </span>
            <span v-else>
              {{ data.item.serviceCap }}
            </span>
          </template>
          <template #cell(role)="data">
            <span v-if="data.item.role">
              {{ getRoleName(data.item.role) }}
            </span>
          </template>
          <template #cell(actions)="data">
            <div
              v-if="
                data.item.isBot == false &&
                  data.item.role.type != 'default-merchant'
              "
              class="text-nowrap"
            >
              <b-button
                v-b-tooltip.hover
                :to="{
                  name: `details-${route}`,
                  params: { id: data.item.id }
                }"
                :title="$t('general.view')"
                variant="info"
                pill
                size="sm"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="$can('update', permissionType)"
                :to="{
                  name: `update-${route}`,
                  params: { id: data.item.id }
                }"
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('delete', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.delete')"
                variant="danger"
                pill
                size="sm"
                @click="confirmDestroy(data.item.id, data.index, data.item)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
            <div
              v-else
              class="text-nowrap"
            >
              <b-button
                v-b-tooltip.hover
                :to="{
                  name: `details-${route}`,
                  params: { id: data.item.id }
                }"
                :title="$t('general.view')"
                variant="info"
                pill
                size="sm"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="$can('update', permissionType) && data.item.isBot == false"
                v-b-tooltip.hover
                :to="{
                  name: `update-${route}`,
                  params: { id: data.item.id },
                  query: { isBot: data.item.isBot }
                }"
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('delete', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.defaultRoleRule')"
                variant="secondary"
                pill
                size="sm"
              >
                <feather-icon icon="LockIcon" />
              </b-button>
            </div>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormGroup,
  BInputGroup,
  BAvatar,
  BBadge,
  BLink,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import NTable from '@/components/NTable'
import { avatarText } from '@core/utils/filter'
import useJwt from '@/auth/jwt/useJwt'
import NButtonLoading from '@/components/NButtonLoading'
import useAppStore from '@/store-pinia/useAppStore'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import SearchInputs from './searchInput'
import tableFields from './tableField'
import botType from '@/data/botType'

const OperatorRepository = Repository.get('operator')
const NotificationConfigRepository = Repository.get('notificationConfig')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BAvatar,
    BLink,
    BFormGroup,
    BInputGroup,
    BBadge,
    BSpinner,
    NPagination,
    NSearchInput,
    NSearchContainer,
    NTable,
    vSelect,
    NButtonLoading,
  },

  data() {
    return {
      botType,
      items: [],
      loading: false,
      resendLoading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
        roleId: this.$route.query.roleId || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
        roleId: this.$route.query.roleId || null,
      },
      avatarText,
      loadingUnbindTelegram: false,
      selectedUserId: '',
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return !!this.$route.query.name || !!this.$route.query.isEnable
    },
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    locale() {
      return this.$i18n.locale.split('-')[0]
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    'appStore.isBoundTelegram': {
      handler() {
        this.getData()
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.getData(this.query.page)
  },
  methods: {
    getRoleName(role) {
      const r = role?.name
      if (typeof r === 'string') {
        return r === 'Super Admin' ? this.$t('field.superAdmin') : r;
      }
      return r?.[this.$i18n.locale.split('-')[0]] || r?.en || r?.zh || r?.vi || r?.km || '-'
    },
    copyToClipboard(text) {
      this.$copyText(text).then(
        () => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('general.copied'),
              icon: 'CopyIcon',
            },
          })
        },
        () => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('general.cannotCopy'),
              icon: 'AlertCircleIcon',
            },
          })
        },
      )
    },
    handleBindTelegram(bind, userId) {
      this.selectedUserId = userId
      if (bind) {
        window.open(this.userData.bindTelegramUrl, '_blank')
        return
      }
      this.loadingUnbindTelegram = true
      NotificationConfigRepository.unbindTelegram(userId)
        .then(data => {
          const d = data.data.data
          this.appStore.setBoundTelegram(false)
          const index = this.items.findIndex(item => item.id === userId)
          if (index !== -1) {
            this.items.splice(index, 1, { ...this.items[index], ...d })
          }
        })
        .finally(() => {
          this.loadingUnbindTelegram = false
        })
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      OperatorRepository.index({
        ...this.query,
      })
        .then(response => {
          const data = response.data.data.list
          // console.log("data", data);
          this.items = [...data]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },
    resend(email, idx) {
      this.resendLoading = true
      OperatorRepository.resend({
        email,
      })
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.items[idx].isActive = data.isActive
            this.items[idx].phone = data.phone
            this.items[idx].name = `${data.firstName ? data.firstName : ''} ${
              data.lastName ? data.lastName : ''
            }`
          }
        })
        .catch(error => {})
        .then(() => {
          this.resendLoading = false
        })
    },
    confirmDestroy(id, index, data) {
      index = this.$refs.table.calculateOrderNumber(index)

      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: `#${index} ${data.name}`,
          }),
          {
            title: this.$t('alert.confirmContinueOperation'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.yesDelete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.destroy(id)
          }
        })
    },
    destroy(id) {
      OperatorRepository.destroy(id)
        .then(response => {
          this.getData()
        })
        .catch(error => {})
        .then(() => {
          this.loading = false
        })
    },
    getDepartmentName(data) {
      let name = data?.name
      if (typeof name === 'object') {
        name = name[this.locale] || name.zh || name.en || name.vn || name.km || '-'
      }
      return name
    },
  },
  setup() {
    const fields = tableFields
    const searchFields = SearchInputs
    const permissionType = 'manage-users'
    const route = 'operator'
    const appStore = useAppStore()

    return {
      fields,
      searchFields,
      permissionType,
      route,
      appStore,
    }
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
</style>
